import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CartContext = createContext(null);
const LS_KEY = "cbr_cart_v1";
const key = (i) => `${i.product_id}::${i.variant_id || ""}`;

function loadLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((i) => i && i.product_id) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(loadLocal); // [{product_id, variant_id, qty}]
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [country, setCountry] = useState("CA");
  const [province, setProvince] = useState("");
  const [shippingMethod, setShippingMethod] = useState(null);
  const merged = useRef(false);
  const debounce = useRef(null);

  // persist minimal cart locally
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  // merge guest cart into server cart on login, then adopt server cart
  useEffect(() => {
    if (!user) { merged.current = false; return; }
    if (merged.current) return;
    merged.current = true;
    (async () => {
      try {
        const { data } = await api.post("/marketplace/cart/merge", { items: loadLocal() });
        setItems((data.items || []).map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })));
      } catch { /* ignore */ }
    })();
  }, [user]);

  // sync to server when logged in
  const syncServer = useCallback(async (next) => {
    if (!user) return;
    try { await api.put("/marketplace/cart", { items: next }); } catch { /* ignore */ }
  }, [user]);

  const refreshQuote = useCallback(async (list) => {
    const src = list || items;
    if (!src.length) { setQuote(null); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/marketplace/cart/quote", {
        items: src.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
        discount_code: discountCode || undefined,
        country, province: province || undefined, shipping_method: shippingMethod || undefined,
      });
      setQuote(data);
      // reconcile qty/removals from server truth
      const reconciled = data.lines.map((l) => ({ product_id: l.product_id, variant_id: l.variant_id, qty: l.qty }));
      const changed = JSON.stringify(reconciled) !== JSON.stringify(src);
      if (changed) { setItems(reconciled); syncServer(reconciled); }
    } finally {
      setLoading(false);
    }
  }, [items, discountCode, country, province, shippingMethod, syncServer]);

  // debounced quote on any input change
  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => refreshQuote(items), 250);
    return () => clearTimeout(debounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items), discountCode, country, province, shippingMethod]);

  const commit = (next) => { setItems(next); syncServer(next); };

  const addItem = (item, { openDrawer = true } = {}) => {
    const k = key(item);
    setItems((prev) => {
      const existing = prev.find((i) => key(i) === k);
      let next;
      if (existing) next = prev.map((i) => (key(i) === k ? { ...i, qty: Math.min(99, i.qty + (item.qty || 1)) } : i));
      else next = [...prev, { product_id: item.product_id, variant_id: item.variant_id, qty: item.qty || 1 }];
      syncServer(next);
      return next;
    });
    if (openDrawer) setOpen(true);
  };

  const updateQty = (k, qty) => commit(items.map((i) => (key(i) === k ? { ...i, qty: Math.max(1, Math.min(99, qty)) } : i)));
  const removeItem = (k) => commit(items.filter((i) => key(i) !== k));
  const clear = () => commit([]);

  const count = items.reduce((n, i) => n + (i.qty || 0), 0);

  return (
    <CartContext.Provider value={{
      items, count, open, setOpen, quote, loading,
      discountCode, setDiscountCode, country, setCountry, province, setProvince,
      shippingMethod, setShippingMethod,
      addItem, updateQty, removeItem, clear, refreshQuote, keyOf: key,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
}
